import React from 'react';
import { observer } from 'mobx-react';
import { observable } from 'mobx';
import store from '../../store';
import { formatDate, trimmedName } from './tools';
import { isProjectWithdrawn, getWithdrawalAttachmentsList } from 'root/common/utils/stageListWithdrawal';
import {
	toArr,
	getStageListRoot,
	getStages,
	getActions,
	getDocListRows,
	getActionDescription,
	getZasedanieDate,
} from '../../utils/stageListAdapter';
import './style.scss';
import './PassportStageList.scss';

//* компонент рисует дерево под новую синхронизацию через stageList

/** Собирает все fileId из RefFile в дереве stageList (для предзапроса SazdFile). */
function collectFileIdsFromTree(sl) {
	const ids = new Set();
	function walk(node) {
		if (!node) return;
		if (node.RefFile) toArr(node.RefFile).forEach(f => ids.add(Number(f.fileId)));
		getDocListRows(node.DocList).forEach(row => {
			if (row && row.RefFile) toArr(row.RefFile).forEach(f => ids.add(Number(f.fileId)));
		});
		getActions(node).forEach(walk);
	}
	getStages(sl).forEach(stage => walk(stage));
	return Array.from(ids).filter(id => Number.isFinite(id) && id > 0);
}

/** Есть ли хотя бы один именованный потомок (ActionName) в поддереве. */
function hasNamedDescendants(node) {
	return getActions(node).some(c => !!(c.ActionName || '').trim() || hasNamedDescendants(c));
}

/**
 * Рекурсивно собирает все RefFile из поддерева.
 * Сохраняет DocUID чтобы потом определить, является ли файл текущим документом.
 */
function collectRefFilesDeep(node) {
	const files = [];
	if (node.RefFile) {
		toArr(node.RefFile).forEach(f => files.push({ ...f, _docUID: node.DocUID }));
	}
	getDocListRows(node.DocList).forEach(row => {
		if (row.RefFile) {
			toArr(row.RefFile).forEach(f => files.push({ ...f, _docUID: row.DocUID }));
		}
	});
	getActions(node).forEach(child => files.push(...collectRefFilesDeep(child)));
	return files;
}

/**
 * Рекурсивно обходит дерево actions и добавляет плоские элементы в items.
 * depth 1 → level 2, depth 2+ → level 3.
 * Узлы без ActionName прозрачны (проходим детей с тем же depth).
 * Файлы показываем только у leaf-узлов (нет named потомков).
 */
const SKIP_ACTION_NAMES = [];

function collectActionItems(action, depth, items, docUid) {
	const name = (action.ActionName || '').trim();
	const children = getActions(action);
	const docRows = getDocListRows(action.DocList);

	if (SKIP_ACTION_NAMES.includes(name)) {
		children.forEach(child => collectActionItems(child, depth, items, docUid));
		return;
	}

	if (name) {
		const level = depth <= 1 ? 2 : 3;
		const rawDate = level === 2 ? getZasedanieDate(action, docRows) : null;
		const isLeaf = !hasNamedDescendants(action);
		const files = isLeaf ? collectRefFilesDeep(action) : [];
		const desc = getActionDescription(action, docRows);
		items.push({
			level,
			date: rawDate ? formatDate(rawDate) : null,
			title: name,
			description: desc,
			files,
			docUid,
		});
		children.forEach(child => collectActionItems(child, depth + 1, items, docUid));
	} else {
		// Прозрачный узел — дети наследуют текущий depth
		children.forEach(child => collectActionItems(child, depth, items, docUid));
	}
}

@observer
export default class PassportStageList extends React.Component {
	@observable sazdFilesMap = {};
	@observable filesLoaded = false;

	componentDidMount() {
		this.loadSazdFiles();
	}

	componentDidUpdate(prevProps) {
		if (prevProps.record !== this.props.record) {
			this.loadSazdFiles();
		}
	}

	loadSazdFiles = async () => {
		const record = this.props.record;
		const sl = getStageListRoot(record && record.stageList);
		this.filesLoaded = false;
		if (!sl) {
			this.sazdFilesMap = {};
			this.filesLoaded = true;
			return;
		}
		const fileIds = collectFileIdsFromTree(sl);
		if (fileIds.length === 0) {
			this.sazdFilesMap = {};
			this.filesLoaded = true;
			return;
		}
		try {
			const list = await store.model.SazdFile.find({ where: { externalId: { inq: fileIds } } });

			const map = {};
			(list || []).forEach(f => {
				map[Number(f.externalId)] = f;
			});
			this.sazdFilesMap = map;
			this.filesLoaded = true;
		} catch (e) {
			this.sazdFilesMap = {};
			this.filesLoaded = true;
		}
	};

	cutExtension = filename =>
		filename && filename.search(/\./) !== -1
			? filename
					.split('.')
					.slice(0, -1)
					.join('.')
			: filename || '';

	getSazdFileUrl = id => {
		const record = new store.model.SazdFile({ id });
		return record.downloadFile('filename');
	};

	isYakFile = label => {
		const name = (label || '').trim().toLowerCase();
		return name.startsWith('як.');
	};

	sortFiles = files => {
		if (!files || files.length <= 1) return files;
		return [...files].sort((a, b) => {
			const aLabel = a.description || 'fileId ' + a.fileId;
			const bLabel = b.description || 'fileId ' + b.fileId;
			const aYak = this.isYakFile(aLabel);
			const bYak = this.isYakFile(bLabel);
			if (aYak === bYak) return 0;
			return aYak ? 1 : -1;
		});
	};

	renderFile = (file, idx) => {
		const sazdFile = this.sazdFilesMap[Number(file.fileId)];
		const url = sazdFile ? this.getSazdFileUrl(sazdFile.id) : null;
		const label = file.description || 'fileId ' + file.fileId;
		const isYak = this.isYakFile(label);
		return (
			<div className={`psl-doc-file${isYak ? ' psl-doc-file--yak' : ''}`} key={idx}>
				{url ? (
					<a target="_blank" rel="noopener noreferrer" href={url}>
						{this.cutExtension(label)}
					</a>
				) : (
					this.cutExtension(label)
				)}
			</div>
		);
	};

	getAttachmentUrl = attachment => {
		if (!attachment || attachment.id == null) return null;
		if (typeof attachment.downloadFile === 'function') {
			return attachment.downloadFile('filename');
		}
		return new store.model.Attachment({ id: attachment.id, filename: attachment.filename }).downloadFile('filename');
	};

	renderWithdrawalAttachment = (attachment, idx) => {
		const url = this.getAttachmentUrl(attachment);
		const label = attachment.name || attachment.filename || 'Файл';
		if (!url) return null;
		return (
			<div className="psl-doc-file" key={`withdrawal-${attachment.id || idx}`}>
				<a target="_blank" rel="noopener noreferrer" href={url}>
					{this.cutExtension(label)}
				</a>
			</div>
		);
	};

	shouldShowWithdrawalFiles = (item, showOnZPResult) => {
		const attachments = getWithdrawalAttachmentsList(this.props.record);
		if (!attachments.length) return false;
		if (showOnZPResult && item.content && isProjectWithdrawn(item.content)) return true;
		if (!showOnZPResult && (isProjectWithdrawn(item.title) || isProjectWithdrawn(item.description))) return true;
		return false;
	};

	render() {
		const record = this.props.record;
		const sl = getStageListRoot(record && record.stageList);
		if (!sl) return null;
		if (!this.filesLoaded) return <div className="psl-loading">Загрузка файлов…</div>;

		const docUid = record.externalId != null ? String(record.externalId) : null;
		const stages = getStages(sl);
		const showWithdrawalOnZPResult = isProjectWithdrawn(record.ZPResult);
		const withdrawalAttachments = getWithdrawalAttachmentsList(record);

		const items = [];
		stages.forEach((stage, stageIdx) => {
			const title = stage.StageAnnotat || 'Этап ' + (stageIdx + 1);
			items.push({ level: 1, date: null, title, files: [], docUid });
			getActions(stage).forEach(action => {
				collectActionItems(action, 1, items, docUid);
			});
		});

		if (record.ZPResult && record.ZPResult !== 'null') {
			items.push({ level: 1, date: null, title: 'Результат по ЗП', content: record.ZPResult, files: [] });
		}

		const lastIdx = items.length - 1;

		return (
			<>
				<div className="doc-name">
					<strong>{trimmedName(record.name)}</strong>
				</div>
				<div className="passport">
					<div className="psl-timeline">
						{items.map((item, idx) => (
							<div key={idx} className={`psl-row psl-row--l${item.level}`}>
								<div className="psl-row__date">{item.date && <span>{item.date}</span>}</div>
								<div className="psl-row__axis">
									<div className="psl-row__dot-wrap">
										<div className="psl-row__dot" />
									</div>
									{idx < lastIdx && <div className="psl-row__connector" />}
								</div>
								<div className="psl-row__content">
									{item.title && (
										<strong className={isProjectWithdrawn(item.title) ? 'psl-withdrawn' : undefined}>{item.title}</strong>
									)}
									{item.description && (
										<div className={`psl-row__description${isProjectWithdrawn(item.description) ? ' psl-withdrawn' : ''}`}>
											{item.description}
										</div>
									)}
									{item.content && (
										<div className={isProjectWithdrawn(item.content) ? 'psl-withdrawn' : undefined}>{item.content}</div>
									)}
									{item.files && item.files.length > 0 && (
										<div className="psl-doc-files">{this.sortFiles(item.files).map((file, fi) => this.renderFile(file, fi))}</div>
									)}
									{withdrawalAttachments.length > 0 &&
										this.shouldShowWithdrawalFiles(item, showWithdrawalOnZPResult) && (
											<div className="psl-doc-files">
												{withdrawalAttachments.map((a, i) => this.renderWithdrawalAttachment(a, i))}
											</div>
										)}
								</div>
							</div>
						))}
					</div>
				</div>
			</>
		);
	}
}
